import { useQuery } from '@apollo/client';
import { GetCollection, GetMarketplaceFilters } from '@atlassian/mpac-services-client';
import { useEffect, useRef } from 'react';

import type { ApolloClient } from '@apollo/client';
import type { TileData } from '@atlassian/mpac-app-tile';
import type {
  GetCollectionQuery,
  GetCollectionQueryVariables,
  GetMarketplaceFiltersQuery,
  GetMarketplaceFiltersQueryVariables,
} from '@atlassian/mpac-services-client';
import type {
  FilterId,
  MarketplaceFilterOption,
} from '@atlassian/mpac-types/client-graphql-schema-types';

interface UseCollectionCategoryDataProps {
  collectionId: string;
  categoryId: string;
  allCategoryIds?: string[];
  shouldPrefetchCategories?: boolean;
}

interface UseCollectionCategoryDataReturn {
  apps: TileData[] | undefined;
  loading: boolean;
  error: unknown;
}

const getRandomApps = (appsArray: TileData[] | undefined, count = 2): TileData[] => {
  if (!appsArray || appsArray.length === 0) return [];
  if (appsArray.length <= count) return appsArray;

  const shuffled = [...appsArray];

  // Fisher-Yates shuffle algorithm
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled.slice(0, count);
};

const prefetchCategory = async (
  client: ApolloClient<any>,
  collectionId: string,
  category: MarketplaceFilterOption,
  categoryId: string,
  randomAppsRef: React.MutableRefObject<Record<string, TileData[]>>
) => {
  const result = await client.query<GetCollectionQuery, GetCollectionQueryVariables>({
    query: GetCollection,
    variables: {
      slug: collectionId,
      sort: 'relevance',
      after: 0,
      first: 50,
      skipCollection: true,
      filters: {
        categories: category ? [category] : undefined,
      },
    },
  });

  const apps = result.data?.marketplaceCollection?.apps?.edges?.map(
    (edge) => edge.node as TileData
  );
  const key = collectionId + categoryId;
  if (apps?.length && !randomAppsRef.current[key]) {
    randomAppsRef.current[key] = getRandomApps(apps);
  }
};

export const useCollectionsData = ({
  collectionId,
  categoryId,
  allCategoryIds = [],
  shouldPrefetchCategories = false,
}: UseCollectionCategoryDataProps): UseCollectionCategoryDataReturn => {
  const randomAppsRef = useRef<Record<string, TileData[]>>({});
  const prefetchedRef = useRef<boolean>(false);

  const {
    data: filterOptionsData,
    loading: filterOptionsLoading,
    error: filterOptionsError,
  } = useQuery<GetMarketplaceFiltersQuery, GetMarketplaceFiltersQueryVariables>(
    GetMarketplaceFilters,
    {
      variables: {
        filterIds: ['categories'] as FilterId[],
      },
    }
  );

  const categoriesFilters = filterOptionsData?.marketplaceFilters?.find(
    (filter) => filter.filterId === 'categories'
  );

  const selectedCategory =
    categoriesFilters?.__typename === 'MarketplaceFilterOptions'
      ? categoriesFilters.options.find((option) => option.id === categoryId)
      : '';

  // Query for the current active category
  const {
    data: collectionData,
    loading: collectionLoading,
    error: collectionError,
    client,
  } = useQuery<GetCollectionQuery, GetCollectionQueryVariables>(GetCollection, {
    variables: {
      slug: collectionId || '',
      sort: 'relevance',
      after: 0,
      first: 50,
      skipCollection: true,
      filters: {
        categories: selectedCategory ? [selectedCategory] : undefined,
      },
    },
    skip: !selectedCategory || !collectionId,
  });

  // Prefetch all categories except the current one
  useEffect(() => {
    if (
      shouldPrefetchCategories &&
      !prefetchedRef.current &&
      categoriesFilters?.__typename === 'MarketplaceFilterOptions' &&
      allCategoryIds.length > 0 &&
      collectionId
    ) {
      prefetchedRef.current = true;

      allCategoryIds.forEach((catId) => {
        if (catId !== categoryId) {
          const category = categoriesFilters.options.find((option) => option.id === catId);
          if (category) {
            prefetchCategory(client, collectionId, category, catId, randomAppsRef);
          }
        }
      });
    }
  }, [
    shouldPrefetchCategories,
    allCategoryIds,
    categoryId,
    collectionId,
    categoriesFilters,
    client,
  ]);

  const loading = collectionLoading || filterOptionsLoading;
  const error = collectionError || filterOptionsError;

  const apps = collectionData?.marketplaceCollection?.apps?.edges?.map(
    (edge) => edge.node as TileData
  );

  const categoryCollectionKey = collectionId + categoryId;

  // randomAppsRef ensures same set of random apps is served for the same collection and category
  if (!randomAppsRef.current[categoryCollectionKey] && apps?.length) {
    randomAppsRef.current[categoryCollectionKey] = getRandomApps(apps);
  }

  return {
    apps: randomAppsRef.current[categoryCollectionKey] || [],
    loading,
    error,
  };
};
